import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ServiceCard from './components/ServiceCard';
import PricingSection from './components/PricingSection';
import { useSEO } from '../../utils/seo';

export default function Services() {
  useSEO({
    title: 'Nos Services de Coaching Sportif - Musculation, Yoga, Nutrition & Plus',
    description: 'Découvrez nos services de coaching sportif personnalisés : musculation, yoga, pilates, nutrition, cardio, HIIT, sport prénatal et coaching senior. Programmes adaptés à tous les niveaux à partir de 45€.',
    keywords: 'coaching sportif, musculation, yoga, pilates, nutrition, cardio, HIIT, sport prénatal, coaching senior, préparation sportive',
    canonical: '/services',
    ogImage: 'https://public.readdy.ai/ai/img_res/e9014c59a5e3dfa83e7bd713c7a5b88e.jpg',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Services de Coaching Sportif CoachFit",
      "description": "Services de coaching sportif personnalisés incluant musculation, yoga, nutrition, cardio et HIIT",
      "provider": {
        "@type": "Organization",
        "name": "CoachFit",
        "url": `${import.meta.env.VITE_SITE_URL || ''}`
      },
      "areaServed": {
        "@type": "Country",
        "name": "France"
      },
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "EUR",
        "lowPrice": "45",
        "highPrice": "80",
        "offerCount": "9"
      }
    }
  });

  const services = [
    {
      id: 'musculation',
      icon: "ri-boxing-line",
      title: "Musculation & Force",
      description: "Développez votre masse musculaire et votre force avec des programmes d'entraînement personnalisés adaptés à votre niveau et vos objectifs.",
      image: "https://readdy.ai/api/search-image?query=Professional%20muscular%20athlete%20performing%20barbell%20deadlift%20exercise%20in%20modern%20minimalist%20gym%20with%20natural%20lighting%2C%20focused%20determined%20expression%2C%20perfect%20form%20technique%20demonstration%2C%20clean%20white%20walls%2C%20professional%20fitness%20photography%20with%20shallow%20depth%20of%20field%2C%20motivational%20strength%20training%20atmosphere&width=800&height=600&seq=service-musculation&orientation=landscape",
      features: [
        "Programme de prise de masse",
        "Renforcement musculaire ciblé",
        "Techniques d'haltérophilie",
        "Suivi de progression détaillé"
      ],
      price: "À partir de 65€/séance"
    },
    {
      id: 'yoga',
      icon: "ri-heart-pulse-line",
      title: "Yoga & Pilates",
      description: "Améliorez votre souplesse, votre équilibre et votre bien-être mental avec des séances de yoga et pilates adaptées à tous les niveaux.",
      image: "https://readdy.ai/api/search-image?query=Serene%20woman%20practicing%20yoga%20warrior%20pose%20on%20minimalist%20white%20mat%20in%20bright%20airy%20studio%20with%20natural%20sunlight%2C%20peaceful%20meditation%20atmosphere%2C%20elegant%20athletic%20wear%2C%20professional%20wellness%20photography%20with%20soft%20lighting%2C%20zen%20mindfulness%20and%20balance%20concept&width=800&height=600&seq=service-yoga&orientation=landscape",
      features: [
        "Yoga Vinyasa et Hatha",
        "Pilates matwork",
        "Méditation et respiration",
        "Amélioration de la posture"
      ],
      price: "À partir de 60€/séance"
    },
    {
      id: 'nutrition',
      icon: "ri-apple-line",
      title: "Nutrition & Diététique",
      description: "Optimisez vos résultats avec un accompagnement nutritionnel personnalisé et des plans alimentaires adaptés à vos objectifs.",
      image: "https://readdy.ai/api/search-image?query=Beautifully%20arranged%20healthy%20meal%20prep%20bowls%20with%20colorful%20fresh%20vegetables%20grilled%20chicken%20quinoa%20avocado%20on%20clean%20white%20marble%20surface%2C%20professional%20food%20photography%20with%20natural%20lighting%2C%20nutritious%20balanced%20diet%20concept%2C%20vibrant%20colors%20and%20minimalist%20composition&width=800&height=600&seq=service-nutrition&orientation=landscape",
      features: [
        "Bilan nutritionnel complet",
        "Plans de repas personnalisés",
        "Conseils pour perte de poids",
        "Suivi hebdomadaire"
      ],
      price: "À partir de 70€/consultation"
    },
    {
      id: 'cardio',
      icon: "ri-run-line",
      title: "Cardio & Endurance",
      description: "Améliorez votre condition cardiovasculaire et votre endurance avec des entraînements variés et progressifs.",
      image: "https://readdy.ai/api/search-image?query=Dynamic%20athletic%20runner%20sprinting%20on%20outdoor%20track%20at%20sunrise%20with%20motion%20blur%20effect%2C%20determined%20focused%20expression%2C%20professional%20sports%20photography%20with%20golden%20hour%20lighting%2C%20energetic%20cardio%20training%20atmosphere%2C%20clean%20minimalist%20background&width=800&height=600&seq=service-cardio&orientation=landscape",
      features: [
        "Course à pied et running",
        "HIIT et interval training",
        "Circuit training",
        "Préparation physique"
      ],
      price: "À partir de 60€/séance"
    },
    {
      id: 'hiit',
      icon: "ri-fire-line",
      title: "HIIT & Functional Training",
      description: "Brûlez des calories et tonifiez votre corps avec des entraînements intensifs et fonctionnels de haute intensité.",
      image: "https://readdy.ai/api/search-image?query=Intense%20fitness%20athlete%20performing%20explosive%20box%20jump%20exercise%20in%20modern%20industrial%20gym%2C%20powerful%20dynamic%20movement%20captured%20mid-air%2C%20dramatic%20lighting%20with%20motion%20energy%2C%20professional%20action%20sports%20photography%2C%20high%20intensity%20interval%20training%20atmosphere&width=800&height=600&seq=service-hiit&orientation=landscape",
      features: [
        "Entraînements HIIT",
        "Functional training",
        "Bootcamp à domicile",
        "Exercices au poids du corps"
      ],
      price: "À partir de 65€/séance"
    },
    {
      id: 'prenatal',
      icon: "ri-parent-line",
      title: "Sport Prénatal & Postnatal",
      description: "Accompagnement spécialisé pour les femmes enceintes et jeunes mamans avec des exercices adaptés et sécurisés.",
      image: "https://readdy.ai/api/search-image?query=Peaceful%20pregnant%20woman%20practicing%20gentle%20prenatal%20yoga%20stretching%20on%20exercise%20mat%20in%20bright%20serene%20home%20studio%2C%20soft%20natural%20lighting%20through%20windows%2C%20caring%20wellness%20atmosphere%2C%20professional%20maternity%20fitness%20photography%20with%20warm%20tones&width=800&height=600&seq=service-prenatal&orientation=landscape",
      features: [
        "Yoga prénatal",
        "Renforcement du périnée",
        "Remise en forme post-accouchement",
        "Exercices de respiration"
      ],
      price: "À partir de 70€/séance"
    },

    {
      id: 'coaching-senior',
      icon: "ri-user-star-line",
      title: "Coaching Senior",
      description: "Programme adapté aux seniors pour maintenir la forme, l'équilibre et l'autonomie avec des exercices doux et sécurisés.",
      image: "https://readdy.ai/api/search-image?query=Active%20senior%20couple%20practicing%20gentle%20balance%20exercises%20with%20resistance%20bands%20in%20bright%20modern%20fitness%20studio%2C%20smiling%20joyful%20expressions%2C%20professional%20wellness%20photography%20with%20natural%20lighting%2C%20healthy%20aging%20and%20vitality%20concept&width=800&height=600&seq=service-senior&orientation=landscape",
      features: [
        "Gymnastique douce",
        "Prévention des chutes",
        "Maintien de la mobilité",
        "Renforcement musculaire adapté"
      ],
      price: "À partir de 60€/séance"
    },

    {
      id: 'preparation-sportive',
      icon: "ri-trophy-line",
      title: "Préparation Sportive",
      description: "Préparez-vous pour vos compétitions et événements sportifs avec un entraînement ciblé et une planification optimale.",
      image: "https://readdy.ai/api/search-image?query=Determined%20athlete%20training%20with%20agility%20ladder%20and%20cones%20on%20professional%20sports%20field%2C%20focused%20competitive%20preparation%2C%20dynamic%20movement%20photography%20with%20dramatic%20lighting%2C%20elite%20performance%20training%20atmosphere%2C%20clean%20minimalist%20background&width=800&height=600&seq=service-preparation&orientation=landscape",
      features: [
        "Préparation marathon/trail",
        "Entraînement spécifique sport",
        "Planification périodisée",
        "Tests de performance"
      ],
      price: "À partir de 75€/séance"
    },

    {
      id: 'coaching-duo-groupe',
      icon: "ri-team-line",
      title: "Coaching en Duo/Groupe",
      description: "Entraînez-vous avec vos proches ou amis pour plus de motivation et des tarifs avantageux.",
      image: "https://readdy.ai/api/search-image?query=Group%20of%20diverse%20friends%20training%20together%20with%20kettlebells%20in%20bright%20modern%20gym%2C%20laughing%20and%20motivating%20each%20other%2C%20energetic%20team%20workout%20atmosphere%2C%20professional%20fitness%20photography%20with%20natural%20lighting%2C%20community%20and%20friendship%20concept&width=800&height=600&seq=service-groupe&orientation=landscape",
      features: [
        "Séances en duo",
        "Petits groupes (3-5 personnes)",
        "Ambiance conviviale",
        "Tarifs dégressifs"
      ],
      price: "À partir de 45€/personne"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section 
          className="relative py-32 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20modern%20luxury%20fitness%20center%20with%20diverse%20people%20training%2C%20professional%20coaches%20demonstrating%20exercises%2C%20bright%20spacious%20gym%20with%20floor-to-ceiling%20windows%2C%20natural%20sunlight%20streaming%20through%2C%20motivational%20athletic%20atmosphere%20with%20clean%20minimalist%20design%2C%20professional%20sports%20photography&width=1920&height=800&seq=services-hero-new&orientation=landscape')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Nos Types de Coaching
            </h1>
            <p className="text-xl md:text-2xl text-gray-100 max-w-3xl mx-auto leading-relaxed">
              Découvrez notre gamme complète de services de coaching sportif personnalisés pour tous les niveaux et tous les objectifs
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Nos Spécialités de Coaching
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Chaque service est adapté à vos besoins spécifiques avec un accompagnement personnalisé à domicile
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <ServiceCard 
                  key={service.id} 
                  {...service}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Notre Méthode de Coaching
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Une approche structurée en 5 étapes pour garantir votre réussite et des résultats durables
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-list-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Bilan initial</h3>
                <p className="text-gray-600 text-sm">Évaluation de votre condition physique et définition de vos objectifs</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-route-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Programme sur mesure</h3>
                <p className="text-gray-600 text-sm">Création d'un plan d'entraînement personnalisé adapté à vos besoins</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-rocket-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Séances coaching</h3>
                <p className="text-gray-600 text-sm">Entraînements réguliers avec votre coach à domicile</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-line-chart-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4. Suivi progrès</h3>
                <p className="text-gray-600 text-sm">Mesure régulière de vos performances et ajustements du programme</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-trophy-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">5. Objectifs atteints</h3>
                <p className="text-gray-600 text-sm">Célébration de vos succès et maintien des résultats</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <PricingSection />

        {/* CTA Section */}
        <section className="py-16 bg-blue-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">
              Prêt à commencer votre transformation ?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Réservez votre première séance de coaching et découvrez votre potentiel
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/coachs" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                Choisir un coach
              </a>
              <a href="/contact" className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">
                Nous contacter
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
